import cn from 'classnames';
import { useCallback, useEffect } from 'react';
import ReactModal from 'react-modal';

import { useEsc } from '@webapp/common/hooks/use-esc';
import { useLockScroll } from '@webapp/common/hooks/use-lock-scroll';

import { ModalContent } from './modal-content';
import type { ModalProps } from './types';

import css from './modal.css';

export const Modal: FC<ModalProps> = ({
    acceptText,
    cancelText,
    children,
    className,
    closeBtn = true,
    onAccept,
    onCancel,
    onClose,
    onPurge,
    onRemove,
    portalClassName,
    overlayClassName,
    style,
    title,
    visible,
    titleAlign,
    disableOutsideClick,
    parentSelector,
    disableAcceptBtn,
}) => {
    const { disable, enable } = useLockScroll();

    const close = useCallback(() => {
        enable();
        onClose?.();
    }, [enable]);

    useEsc(close);

    useEffect(() => {
        if (visible) {
            disable();
        } else {
            enable();
        }

        return () => {
            if (visible) {
                enable();
            }
        };
    }, [disable, enable, visible]);

    return (
        <ReactModal
            className={cn(css.modal, className)}
            closeTimeoutMS={500}
            isOpen={visible}
            overlayClassName={{
                base: cn(css.overlay, overlayClassName),
                afterOpen: css.opened,
                beforeClose: css.closing,
            }}
            portalClassName={portalClassName}
            parentSelector={parentSelector}
            onRequestClose={() => {
                if (!disableOutsideClick) {
                    close();
                }
            }}
            style={{ content: style }}
            appElement={document.getElementById('app') || document.getElementById('__next')}
        >
            <ModalContent
                title={title}
                closeBtn={closeBtn}
                titleAlign={titleAlign}
                acceptText={acceptText}
                cancelText={cancelText}
                onAccept={onAccept}
                onClose={close}
                onCancel={onCancel}
                onRemove={onRemove}
                onPurge={onPurge}
                disableAcceptBtn={disableAcceptBtn}
            >
                {children}
            </ModalContent>
        </ReactModal>
    );
};
